/**
 * Le module **Clients** — la liste que la maquette dessine, et que l'écran
 * précédent ne pouvait pas montrer (`B-05`, `EX-BO-004`).
 *
 * Maquette : artboard `b_clients` de
 * `designs/client-app/project/Jëliko - Guichet, Back-office, Marchand.dc.html`.
 * `test/app/customers-maquette.test.tsx` **relit** cette maquette et la
 * confronte au livré, plutôt que d'en recopier les valeurs ici : un attendu
 * dérivé du code éprouvé n'est pas un attendu (`D-34`).
 *
 * ## Ce qui manquait, et ce n'était pas un écran
 *
 * L'écran existait, et c'était un **formulaire de recherche** : deux champs, un
 * bouton, une liste plate. Les quatre indicateurs du dessin, ses cinq
 * pastilles, ses filtres, son total et sa pagination n'y étaient pas, et sa
 * documentation disait pourquoi — « aucune route ne les sert ». C'était vrai.
 * Les routes sont écrites, et l'écran les montre.
 *
 * ## L'état de l'écran vit dans l'ADRESSE
 *
 * Le mois d'ouverture, le niveau, l'état, la recherche, le motif et la page
 * sont dans la chaîne de requête, et l'écran n'en garde aucune copie. C'est la
 * correction que la recherche de transactions a payée : un second souvenir est
 * un second endroit où le filtre peut être vrai, et c'est ce qui faisait
 * afficher un terme que la table n'appliquait pas.
 *
 * Trois choses en découlent, et les trois valent d'être dites : le retour en
 * arrière rend exactement l'écran d'avant ; une vue filtrée **s'envoie à un
 * collègue** par son adresse ; et la barre de recherche affiche ce que l'écran
 * cherche réellement, parce qu'elle le lit dans la déclaration plutôt que dans
 * son propre souvenir.
 *
 * ## Trois filtres, et non les quatre du dessin
 *
 * Le dessin en porte quatre : Période d'ouverture, Niveau KYC, Statut, **Zone**.
 * Le quatrième n'est pas livré, et ce n'est pas un oubli : `identity.customer`
 * ne porte **aucune** colonne de zone, de région ni d'agence, et rien dans le
 * système ne rattache un client à un territoire. Un filtre « Zone » aurait donc
 * affiché « Dakar » au-dessus d'une base qui n'en sait rien. L'écart est
 * inscrit au registre de la dette de contrôle plutôt que tu — une divergence
 * tue est une divergence que la revue suivante refera.
 *
 * ## La colonne « Client » ne mène PAS par le nom
 *
 * **Quarante-huit fiches sur soixante et une n'ont pas de nom enregistré** —
 * c'est la population du pilote, pas un cas limite. Mener la colonne par le nom
 * aurait écrit « Nom non renseigné » sur quatre lignes sur cinq : exactement le
 * défaut qui a fait retirer la colonne des contreparties de la page
 * Transactions, mesuré là-bas à 56 % de la colonne et 100 % de troncature.
 *
 * La colonne mène donc par la **référence publique**, qui existe toujours et
 * qui est unique, et le nom prend la seconde ligne **quand il existe**. Aucun
 * texte de remplacement n'est imprimé : une ligne qui dit « rien » occupe la
 * place et le regard d'une ligne qui dirait quelque chose.
 *
 * ## Les pastilles sont les ÉTATS que la base déclare
 *
 * Le dessin en pose cinq : Tous · Actifs · Limités · Gelés · Dormants. Les
 * quatre derniers mots sont, littéralement, quatre codes de
 * `identity.customer_status` — `ACTIF`, `LIMITE`, `GELE`, `DORMANT` —, et la
 * table porte leur libellé français depuis la V9. Les pastilles ne sont donc
 * pas une énumération recopiée : ce sont les **facettes servies**, et chacune
 * porte le compte que le serveur a mesuré.
 *
 * « Limité » avait été soupçonné d'être une notion inventée par le dessin. Il
 * ne l'est pas : `identity.customer_status` le déclare depuis la V9 sous le
 * libellé « **Opérations restreintes** », `CustomerStatus.LIMITE` le porte en
 * Java, et la contrainte `customer_status_is_declared` garantit qu'aucun client
 * ne peut être dans un état qui n'y figure pas. La pastille est donc livrée, et
 * elle filtre sur `status = 'LIMITE'` — rien d'inventé. Sur la base de
 * développement **aucune fiche n'est dans cet état**, et la pastille ne paraît
 * donc pas : une facette absente veut dire « aucune fiche ici », jamais « cet
 * état n'existe pas ».
 *
 * ## Aucun calcul dans le navigateur
 *
 * Le total, les quatre cartes, le compte par état, par niveau et par mois
 * sortent tous du même `SELECT` que la page. **Un chiffre calculé dans le
 * navigateur est un chiffre que le journal d'audit ne peut pas justifier**, et
 * deux écrans qui le calculent chacun finissent par diverger.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Pagination } from '../controls/Pagination.js';
import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { QueueShell } from '../shell/QueueShell.js';
import type { QueueColumn, QueueEntry } from '../shell/QueueShell.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import { fetchCustomer, searchCustomers, TOUTES_LES_PERIODES } from '../http/customers.js';
import type { Customer, CustomerPage } from '../http/customers.js';
import { useDeclareSearch } from './search-context.js';
import { useTransactionFormats } from './transactions-display.js';

/**
 * Les six colonnes, **dans l'ordre de la maquette** — et toutes servies.
 *
 * `name` porte la référence publique en tête et le nom en second, pour la
 * raison mesurée en en-tête de ce fichier. Elle garde son identifiant
 * `name` : c'est la colonne « Client » du dessin, et la renommer ferait
 * croire à une colonne différente.
 */
export const customerColumns: readonly QueueColumn[] = [
  { id: 'name', labelKey: 'customers.columns.name', emphasis: 'strong' },
  { id: 'msisdn', labelKey: 'customers.columns.msisdn', emphasis: 'numeric' },
  { id: 'kycLevel', labelKey: 'customers.columns.kycLevel', emphasis: 'numeric' },
  { id: 'balance', labelKey: 'customers.columns.balance', emphasis: 'numeric' },
  { id: 'status', labelKey: 'customers.columns.status', emphasis: 'strong' },
  { id: 'lastActivity', labelKey: 'customers.columns.lastActivity', emphasis: 'muted' },
];

/**
 * Les paramètres que l'écran porte dans l'adresse.
 *
 * Ils sont nommés ici, et une seule fois : la lecture, l'écriture et la remise
 * à zéro s'y réfèrent toutes. Une seconde liste écrite à la main serait un
 * paramètre oublié au premier ajout, et un filtre qui survivrait à une remise à
 * zéro sans que personne le voie.
 */
const PARAMETRES = ['month', 'kycLevel', 'status', 'search', 'reason', 'page'] as const;

/** La première page. */
const PREMIERE_PAGE = 1;

/**
 * Le motif par défaut de la consultation.
 *
 * **L'écran ne l'impose pas : il le propose.** L'écran précédent exigeait que
 * l'agent l'écrive avant de voir quoi que ce soit, et sa documentation disait
 * pourquoi — le journal doit dire *pourquoi* une fiche a été ouverte. La raison
 * reste bonne ; ce qui ne l'était pas est d'avoir fait du motif le **verrou**
 * de l'écran, si bien qu'ouvrir « Clients » ne montrait rien.
 *
 * Le motif est donc un champ du filtre, prérempli et modifiable, et il voyage
 * dans l'adresse comme les autres critères. Un agent qui traite un ticket y met
 * sa référence ; un agent qui parcourt la liste laisse le motif par défaut, et
 * le journal enregistre exactement cela — ce qui est la vérité, et vaut mieux
 * qu'une phrase recopiée machinalement pour franchir un verrou.
 */
const MOTIF_PAR_DEFAUT = 'consultation-du-fichier-client';

/** L'écran : les filtres, les pastilles, les indicateurs, et la table. */
export function CustomersScreen(): ReactNode {
  const { t, i18n } = useTranslation();
  const client = useApiClient();
  const [adresse, poserLAdresse] = useSearchParams();
  const { montant, journeeCourte, heure, instant } = useTransactionFormats();

  const [registre, setRegistre] = useState<CustomerPage | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [fiche, setFiche] = useState<Customer | null>(null);
  const [erreurFiche, setErreurFiche] = useState<string | null>(null);

  /**
   * Ce que l'adresse demande, et **rien d'autre**.
   *
   * La dépendance est la chaîne de requête et non l'objet : `useSearchParams`
   * rend un objet neuf à chaque rendu, et le comparer par référence relancerait
   * une lecture par rendu — donc une entrée d'audit par rendu.
   */
  const requete = adresse.toString();
  const demande = useMemo(() => {
    const lu = new URLSearchParams(requete);
    const rang = Number.parseInt(lu.get('page') ?? '', 10);
    const motif = (lu.get('reason') ?? '').trim();
    return {
      month: lu.get('month') ?? '',
      kycLevel: lu.get('kycLevel') ?? '',
      status: lu.get('status') ?? '',
      search: lu.get('search') ?? '',
      // Un motif vide dans l'adresse retombe sur le défaut plutôt que d'être
      // envoyé vide : le serveur refuserait, et l'écran afficherait une erreur
      // technique là où l'agent n'a fait qu'effacer un champ.
      reason: motif === '' ? MOTIF_PAR_DEFAUT : motif,
      page: Number.isFinite(rang) && rang > 0 ? rang : undefined,
    };
  }, [requete]);

  useEffect(() => {
    let abandonne = false;
    void (async () => {
      try {
        const page = await searchCustomers(client, demande);
        if (!abandonne) {
          setRegistre(page);
          setErreur(null);
        }
      } catch (echec) {
        if (abandonne) return;
        // La page lue est effacée avec l'échec. La garder ferait lire les
        // critères PRÉCÉDENTS sous des filtres nouveaux — sur l'écran où l'on
        // décide de geler un compte, c'est la pire confusion possible.
        setRegistre(null);
        setErreur(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    })();
    return () => {
      abandonne = true;
    };
  }, [client, demande]);

  // La fiche ouverte est refermée dès que les critères changent : la garder
  // ferait lire la fiche d'un client sous les résultats d'un autre.
  useEffect(() => {
    setFiche(null);
    setErreurFiche(null);
  }, [demande]);

  /**
   * Écrit dans l'adresse ce que l'écran doit montrer.
   *
   * Un paramètre vide est **retiré** plutôt qu'écrit vide : `?status=` serait un
   * critère que le serveur devrait interpréter, et une adresse partagée dirait
   * autre chose que ce qu'elle montre.
   *
   * La page repart à un dès qu'un critère change, sauf quand c'est la page
   * elle-même qu'on tourne : garder le rang quatre sous des filtres neufs
   * rendrait une page vide qu'on lirait comme « aucun client ».
   */
  const poser = useCallback(
    (changements: Partial<Record<(typeof PARAMETRES)[number], string>>) => {
      const suivante = new URLSearchParams(requete);
      // La boucle part de la table des paramètres et non des clés reçues : un
      // nom qui ne serait pas de la table n'entre pas dans l'adresse, et une
      // faute de frappe cesse d'y écrire un critère que personne ne relira.
      for (const nom of PARAMETRES) {
        if (!Object.hasOwn(changements, nom)) continue;
        const valeur = changements[nom];
        if (valeur === undefined || valeur === '') suivante.delete(nom);
        else suivante.set(nom, valeur);
      }
      if (!Object.hasOwn(changements, 'page')) suivante.delete('page');
      poserLAdresse(suivante);
    },
    [poserLAdresse, requete],
  );

  const filtrer = useCallback(
    (valeurs: Record<string, string>) => {
      poser({
        month: valeurs.month ?? '',
        kycLevel: valeurs.kycLevel ?? '',
        status: valeurs.status ?? '',
        // Le motif par défaut n'est pas écrit dans l'adresse : l'y mettre
        // ferait porter à chaque lien partagé une phrase que personne n'a
        // choisie, et masquerait les motifs qui, eux, ont été écrits.
        reason:
          (valeurs.reason ?? '').trim() === MOTIF_PAR_DEFAUT
            ? ''
            : (valeurs.reason ?? '').trim(),
      });
    },
    [poser],
  );

  /**
   * Ce que la barre supérieure cherche — **et ce qu'elle affiche**.
   *
   * Le terme déclaré est celui de l'adresse, donc celui que la table applique
   * réellement. La recherche part sur `Entrée` et non à la frappe : chercher
   * est un acte journalisé, et une recherche par frappe écrirait une entrée
   * d'audit par lettre.
   */
  const chercher = useCallback(
    (terme: string) => {
      setAvis(
        terme === ''
          ? t('customers.search.cleared')
          : t('customers.search.applied', { term: terme }),
      );
      poser({ search: terme });
    },
    [poser, t],
  );
  const recherche = useMemo(
    () => ({ term: demande.search, onSearch: chercher }),
    [chercher, demande.search],
  );
  useDeclareSearch(recherche);

  /**
   * Le mois d'ouverture, mis en forme dans la langue de l'agent.
   *
   * Le serveur rend `AAAA-MM` — une valeur, pas un libellé — et le dessin écrit
   * « Sept. 2026 ». La composition en jour 1 à midi UTC évite qu'un fuseau à
   * l'ouest de Greenwich fasse reculer le mois d'un cran.
   */
  const libelleDuMois = useCallback(
    (mois: string): string =>
      new Intl.DateTimeFormat(i18n.language, {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(`${mois}-01T12:00:00Z`)),
    [i18n.language],
  );

  const lignes: readonly QueueEntry[] = useMemo(
    () =>
      (registre?.customers ?? []).map((fichier) => {
        // **Le nom prend la seconde ligne, et SEULEMENT s'il existe.** Aucun
        // texte de remplacement : quarante-huit fiches sur soixante et une
        // n'ont pas de nom, et une ligne qui dit « rien » occuperait la place
        // et le regard d'une ligne qui dirait quelque chose.
        const nom = [fichier.firstName, fichier.lastName]
          .filter((part) => part !== null && part !== '')
          .join(' ');
        const solde =
          fichier.balance === null || fichier.currency === null
            ? t('customers.cells.noAccount')
            : montant(fichier.balance, fichier.currency);
        const activite =
          fichier.lastActivityAt === null
            ? t('customers.cells.neverActive')
            : instant(fichier.lastActivityAt);
        return {
          id: fichier.id,
          reference: fichier.publicRef,
          // `values` reste la vérité TEXTUELLE de la ligne : c'est elle que le
          // résumé du panneau lit, et deux vérités feraient douter des deux.
          values: [
            nom === '' ? fichier.publicRef : `${fichier.publicRef} · ${nom}`,
            fichier.msisdn,
            String(fichier.kycLevel),
            solde,
            fichier.statusLabel,
            activite,
          ],
          cells: [
            <span className="customers__stack" key="name">
              <span className="customers__stack-lead">{fichier.publicRef}</span>
              {nom === '' ? null : <span className="customers__stack-meta">{nom}</span>}
            </span>,
            fichier.msisdn,
            String(fichier.kycLevel),
            // **Le solde se ferre à droite et porte les chiffres tabulaires.**
            // Une colonne de montants qui ne s'aligne pas ne se compare pas.
            <span className="customers__stack customers__stack--end" key="balance">
              <span className="customers__stack-lead">{solde}</span>
            </span>,
            // **Le gel des SORTIES sous la pastille, et jamais confondu avec
            // l'état.** `outbound_frozen_until` porte EX-SEC-007 — un compte
            // dont le numéro vient de changer reste ACTIF, consultable et
            // créditable. Il explique un refus de sortie que rien d'autre
            // n'expliquerait, et il n'entre dans aucun compte de « gelés ».
            <span className="customers__stack" key="status">
              <span className="customers__status" data-tone={fichier.statusTone}>
                {fichier.statusLabel}
              </span>
              {fichier.outboundFrozenUntil === null ? null : (
                <span className="customers__stack-meta">
                  {t('customers.cells.outboundFrozen', {
                    date: journeeCourte(fichier.outboundFrozenUntil),
                  })}
                </span>
              )}
            </span>,
            fichier.lastActivityAt === null ? (
              activite
            ) : (
              <span className="customers__stack" key="lastActivity">
                <span className="customers__stack-lead">
                  {journeeCourte(fichier.lastActivityAt)}
                </span>
                <span className="customers__stack-meta">
                  {heure(fichier.lastActivityAt, true)}
                </span>
              </span>
            ),
          ],
        };
      }),
    [heure, instant, journeeCourte, montant, registre, t],
  );

  /**
   * Les filtres — et **leurs listes viennent du serveur**.
   *
   * Les mois, les niveaux et les états sont les valeurs réellement présentes,
   * avec leur compte et avec le libellé que la base porte. Ce ne sont ni des
   * constantes recopiées, ni des listes inventées ici.
   *
   * Le quatrième filtre du dessin — « Zone » — n'est pas là : aucune colonne du
   * système ne rattache un client à un territoire. Voir l'en-tête.
   */
  const champsDuFiltre: readonly FormFieldDescriptor[] = useMemo(
    () => [
      {
        name: 'month',
        labelKey: 'customers.filters.month',
        control: 'select',
        placeholderKey: 'customers.filters.allMonths',
        options: [
          { value: TOUTES_LES_PERIODES, label: t('customers.filters.allMonths') },
          ...(registre?.months ?? []).map((facette) => ({
            value: facette.month,
            // Le compte est DANS le libellé : un agent choisit d'autant mieux
            // qu'il sait combien il va trouver.
            label: t('customers.filters.option', {
              label: libelleDuMois(facette.month),
              count: facette.count,
            }),
          })),
        ],
      },
      {
        name: 'kycLevel',
        labelKey: 'customers.filters.kycLevel',
        control: 'select',
        placeholderKey: 'customers.filters.allLevels',
        options: [
          { value: '', label: t('customers.filters.allLevels') },
          ...(registre?.levels ?? []).map((facette) => ({
            value: String(facette.level),
            label: t('customers.filters.option', {
              label: t('customers.filters.levelLabel', {
                level: facette.level,
                label: facette.label,
              }),
              count: facette.count,
            }),
          })),
        ],
      },
      {
        name: 'status',
        labelKey: 'customers.filters.status',
        control: 'select',
        placeholderKey: 'customers.filters.allStatuses',
        options: [
          { value: '', label: t('customers.filters.allStatuses') },
          ...(registre?.statuses ?? []).map((facette) => ({
            value: facette.code,
            label: t('customers.filters.option', {
              label: facette.label,
              count: facette.count,
            }),
          })),
        ],
      },
      { name: 'reason', labelKey: 'customers.filters.reason' },
    ],
    [libelleDuMois, registre, t],
  );

  /**
   * Ce que la rangée de filtres **affiche**, quand c'est le serveur qui l'a
   * décidé.
   *
   * Le mois vient de la réponse : on peut lister sans rien choisir, et la
   * pilule doit alors montrer la période que le serveur a retenue plutôt que
   * rester vide sous une table qui, elle, montre un mois précis.
   */
  const valeursDuFiltre = useMemo(
    () =>
      registre === null
        ? undefined
        : {
            month: registre.month ?? TOUTES_LES_PERIODES,
            kycLevel: demande.kycLevel,
            status: demande.status,
            reason: demande.reason,
          },
    [demande, registre],
  );

  // Le nombre de pages se DÉDUIT du total et de la borne — il n'est pas servi,
  // et il n'a pas à l'être. La borne est ramenée à un au minimum, non par
  // défiance mais parce qu'une division par zéro rendrait une infinité de pages.
  const pages =
    registre === null
      ? 1
      : Math.max(1, Math.ceil(registre.total / Math.max(1, registre.pageSize)));

  /**
   * Ouvre une fiche — **et c'est cet appel qui tient la promesse du dessin**.
   *
   * Ce qui est affiché est ce que le serveur vient de rendre, pas la ligne déjà
   * reçue : une fiche montrée sans que le journal la nomme serait exactement la
   * promesse affichée et non tenue.
   */
  const ouvrir = useCallback(
    (ligne: QueueEntry) => {
      setFiche(null);
      setErreurFiche(null);
      void (async () => {
        try {
          setFiche(await fetchCustomer(client, ligne.id, demande.reason));
        } catch (echec) {
          setErreurFiche(echec instanceof ApiError ? echec.messageKey : 'errors.server');
        }
      })();
    },
    [client, demande.reason],
  );

  return (
    <section className="customers" aria-label={t('customers.landmark')}>
      {avis === null ? null : (
        <p className="customers__outcome" role="status" data-testid="customers-outcome">
          {avis}
        </p>
      )}

      {/* La rangée de filtres du dessin : les contrôles et la commande sur UNE
          ligne, en tête de la carte, et non des blocs empilés sur toute la
          largeur. C'est ce qui distingue le plus un écran de back-office d'un
          formulaire. */}
      <FormShell
        fields={champsDuFiltre}
        layout="inline"
        values={valeursDuFiltre}
        onSubmit={filtrer}
        submitLabelKey="customers.filters.submit"
      />

      {/* **Les pastilles d'état, et elles comptent LA MÊME CHOSE que les
          cartes.** Le serveur compte les facettes sur la fenêtre, le niveau et
          la recherche, mais **pas** sur l'état : une pastille choisie qui
          viderait toutes les autres empêcherait de jamais élargir. Le nom du
          groupe le dit, pour que la règle se lise au lieu de se deviner. */}
      <div className="customers__chips" role="group" aria-label={t('customers.chips.landmark')}>
        <button
          type="button"
          className="customers__chip"
          aria-pressed={demande.status === ''}
          onClick={() => {
            poser({ status: '' });
          }}
        >
          {t('customers.chips.all')}
        </button>
        {(registre?.statuses ?? []).map((facette) => (
          <button
            key={facette.code}
            type="button"
            className="customers__chip"
            data-status={facette.code}
            data-tone={facette.tone}
            aria-pressed={demande.status === facette.code}
            onClick={() => {
              poser({ status: facette.code });
            }}
          >
            {t('customers.chips.withCount', {
              label: facette.label,
              count: facette.count,
            })}
          </button>
        ))}
      </div>

      {erreur === null ? null : (
        <p className="customers__notice" role="alert">
          {t(erreur)}
        </p>
      )}

      {registre === null ? (
        erreur === null ? (
          <p className="customers__idle">{t('customers.loading')}</p>
        ) : null
      ) : (
        <>
          <CustomerStats page={registre} />

          {registre.customers.length === 0 ? (
            <p className="customers__idle">{t('customers.empty')}</p>
          ) : (
            <QueueShell
              columns={customerColumns}
              entries={lignes}
              onOpen={ouvrir}
              renderDetail={() => <CustomerDetail entry={fiche} error={erreurFiche} />}
              /* **Le pied du dessin, DANS la carte.** À gauche le compte, à
                 droite la pagination. Le poser sous la carte ferait lire la
                 page comme trois blocs empilés au lieu d'un tableau. */
              footer={
                <Pagination
                  block="customers"
                  keyPrefix="customers.pages"
                  page={registre.page}
                  pages={pages}
                  pageSize={registre.pageSize}
                  onGo={(rang) => {
                    poser({ page: rang === PREMIERE_PAGE ? '' : String(rang) });
                  }}
                  summary={
                    <span className="customers__count" data-testid="customers-count">
                      {t('customers.count', {
                        shown: registre.customers.length,
                        total: registre.total,
                      })}
                    </span>
                  }
                />
              }
            />
          )}
        </>
      )}

      {/* La note du dessin, tenue plutôt qu'affichée : l'ouverture d'une fiche
          appelle vraiment la route qui l'inscrit au journal. La seconde phrase
          — le gel et le déblocage exigent deux validateurs — décrit des actions
          dont AUCUN écran n'est écrit : elle est dite comme un fait du système,
          et non comme la promesse d'un bouton qui n'existe pas. */}
      <p className="customers__journal">{t('customers.journalNotice')}</p>
    </section>
  );
}

/**
 * Les quatre indicateurs du dessin — en **cartes**, et tous servis.
 *
 * « Comptes », « Actifs 30 j », « Gelés », « En attente KYC ». Chacune sort du
 * même `SELECT` que la page, sur les mêmes critères — donc **elles se
 * recalculent avec les filtres**, ce que le porteur a demandé.
 *
 * **« Gelés » compte l'état `GELE`, et surtout pas `outbound_frozen_until`.**
 * La V9 le dit en toutes lettres : ce dernier porte le gel des *sorties* après
 * un changement de numéro, et « le compte reste consultable et créditable ».
 * Les compter ensemble aurait affiché, sous une carte rouge, des comptes
 * parfaitement actifs dont le titulaire a simplement changé de téléphone — sur
 * l'écran où un agent décide d'un gel, c'est exactement le chiffre qu'il ne
 * faut pas lui donner.
 *
 * La fenêtre de la deuxième carte est **servie** (`activeWithinDays`) : le jour
 * où elle passe de trente à soixante, la carte le dira sans qu'une ligne change
 * ici.
 */
function CustomerStats({ page }: { readonly page: CustomerPage }): ReactNode {
  const { t } = useTranslation();

  const cartes: readonly {
    readonly id: string;
    readonly label: string;
    readonly value: string;
    readonly tone?: string;
  }[] = [
    { id: 'accounts', label: t('customers.stats.accounts'), value: String(page.total) },
    {
      id: 'active',
      label: t('customers.stats.active', { days: page.activeWithinDays }),
      value: String(page.activeRecently),
      // Une carte sans mesure ne se peint pas en vert : « aucun compte actif »
      // n'est pas une bonne nouvelle, c'est une absence de nouvelle.
      tone: page.activeRecently === 0 ? undefined : 'OPERATING',
    },
    {
      id: 'frozen',
      label: t('customers.stats.frozen'),
      value: String(page.frozen),
      tone: page.frozen === 0 ? undefined : 'BLOCKED',
    },
    {
      id: 'pendingKyc',
      label: t('customers.stats.pendingKyc'),
      value: String(page.pendingKyc),
      tone: page.pendingKyc === 0 ? undefined : 'RESTRICTED',
    },
  ];

  return (
    <ul className="customers__stats" aria-label={t('customers.stats.landmark')}>
      {cartes.map((carte) => (
        <li className="customers__stat" key={carte.id} data-tone={carte.tone}>
          <span className="customers__stat-label">{carte.label}</span>
          <span className="customers__stat-value" data-testid={`customers-stat-${carte.id}`}>
            {carte.value}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Le panneau d'une fiche ouverte : ce que le **second** appel a rendu.
 *
 * Il ne reçoit pas la ligne de la file, et c'est délibéré : lui donner la ligne
 * l'aurait laissé afficher quelque chose pendant que l'appel journalisé était
 * en cours, ou après son échec — une fiche à l'écran que le journal ne mentionne
 * pas. Tant que l'appel n'a pas rendu, le panneau le **dit**.
 *
 * Les cinq panneaux dépliants du dessin — limites en cours, appareils
 * autorisés, alertes liées, restriction, gel administratif — ne sont servis par
 * aucune route du back-office. L'écart est nommé ici, à l'endroit exact où la
 * revue suivante irait les redessiner avec des valeurs plausibles.
 */
function CustomerDetail({
  entry,
  error,
}: {
  readonly entry: Customer | null;
  readonly error: string | null;
}): ReactNode {
  const { t } = useTranslation();
  const { montant, instant } = useTransactionFormats();
  if (error !== null) {
    return (
      <p className="customers__notice" role="alert">
        {t(error)}
      </p>
    );
  }
  if (entry === null) return <p className="customers__idle">{t('customers.opening')}</p>;
  const nom = [entry.firstName, entry.lastName]
    .filter((part) => part !== null && part !== '')
    .join(' ');
  return (
    <>
      <dl className="customers__facts">
        <dt>{t('customers.detail.publicRef')}</dt>
        <dd data-testid="customer-detail-public-ref">{entry.publicRef}</dd>
        <dt>{t('customers.detail.msisdn')}</dt>
        <dd data-testid="customer-detail-msisdn">{entry.msisdn}</dd>
        {/* Le nom n'est une ligne du panneau QUE s'il existe : un intitulé
            suivi d'un vide poserait une question à laquelle il ne répond pas. */}
        {nom === '' ? null : (
          <>
            <dt>{t('customers.detail.name')}</dt>
            <dd data-testid="customer-detail-name">{nom}</dd>
          </>
        )}
        <dt>{t('customers.detail.status')}</dt>
        <dd data-testid="customer-detail-status">
          <span className="customers__status" data-tone={entry.statusTone}>
            {entry.statusLabel}
          </span>
        </dd>
        <dt>{t('customers.detail.screeningStatus')}</dt>
        <dd data-testid="customer-detail-screening">{entry.screeningStatus}</dd>
        <dt>{t('customers.detail.kycLevel')}</dt>
        <dd data-testid="customer-detail-kyc-level">
          {t('customers.filters.levelLabel', { level: entry.kycLevel, label: entry.kycLabel })}
        </dd>
        <dt>{t('customers.detail.balance')}</dt>
        <dd data-testid="customer-detail-balance">
          {entry.balance === null || entry.currency === null
            ? t('customers.cells.noAccount')
            : montant(entry.balance, entry.currency)}
        </dd>
        <dt>{t('customers.detail.createdAt')}</dt>
        <dd data-testid="customer-detail-created-at">{instant(entry.createdAt)}</dd>
      </dl>
      {/* Les cinq panneaux du dessin — limites, appareils, alertes,
          restriction, gel — n'ont aucune route. L'écart est nommé ici. */}
      <p className="customers__gap">{t('customers.panelsNotice')}</p>
    </>
  );
}
